// Mutations de l'arbre de mise en page (docs/EDITEUR-MISE-EN-PAGE.md, phase 2) : ce que fait
// l'éditeur quand on sélectionne, glisse, scinde, redimensionne ou règle une propriété.
// Fonctions PURES : elles renvoient un nouvel arbre, jamais ne modifient l'entrée. Le canevas ne
// contient aucune logique de mise en page : il ne fait qu'appeler ces fonctions et re-rendre.
import type { FieldId, LayoutBlock, LayoutCol, LayoutRow, Padding, SignatureLayout } from './types.ts';
import { BLOCK_LABELS } from './blocks.ts';

export type BorderSide = 'left' | 'right' | 'top' | 'bottom';

/** Où déposer un bloc : dans une colonne (à l'index donné), dans une nouvelle ligne, ou dans une nouvelle colonne scindée. */
export type DropTarget =
  | { kind: 'block'; colId: string; index: number }
  | { kind: 'row'; index: number }
  | { kind: 'split'; colId: string; side: 'left' | 'right' };

export interface BlockLocation {
  block: LayoutBlock;
  col: LayoutCol;
  row: LayoutRow;
  rowIndex: number;
  colIndex: number;
  blockIndex: number;
}

export const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v));

/** Identifiant sûr, unique dans l'arbre. */
export function newId(layout: SignatureLayout, prefix: string): string {
  const ids = new Set<string>();
  for (const r of layout.rows) {
    ids.add(r.id);
    for (const c of r.cols) {
      ids.add(c.id);
      for (const b of c.blocks) ids.add(b.id);
    }
  }
  let n = 1;
  while (ids.has(`${prefix}${n}`)) n++;
  return `${prefix}${n}`;
}

export function findBlock(layout: SignatureLayout, blockId: string): BlockLocation | null {
  for (const [rowIndex, row] of layout.rows.entries()) {
    for (const [colIndex, col] of row.cols.entries()) {
      const blockIndex = col.blocks.findIndex((b) => b.id === blockId);
      if (blockIndex >= 0) return { block: col.blocks[blockIndex], col, row, rowIndex, colIndex, blockIndex };
    }
  }
  return null;
}

export function findCol(layout: SignatureLayout, colId: string): { col: LayoutCol; row: LayoutRow; rowIndex: number; colIndex: number } | null {
  for (const [rowIndex, row] of layout.rows.entries()) {
    const colIndex = row.cols.findIndex((c) => c.id === colId);
    if (colIndex >= 0) return { col: row.cols[colIndex], row, rowIndex, colIndex };
  }
  return null;
}

/** Retire les colonnes sans bloc ni sous-ligne (largeur en px rendue à une voisine) et les lignes sans colonne. */
export function collapseEmpty(layout: SignatureLayout): SignatureLayout {
  const out = clone(layout);
  out.rows = out.rows
    .map((row) => {
      const kept: LayoutCol[] = [];
      let lostPx = 0;
      for (const col of row.cols) {
        if (col.blocks.length === 0 && !(col.rows && col.rows.length)) {
          if (typeof col.width === 'number') lostPx += col.width;
          continue;
        }
        kept.push(col);
      }
      if (lostPx > 0 && kept.length && !kept.some((c) => c.width === 'auto')) {
        const pxCols = kept.filter((c) => typeof c.width === 'number');
        if (pxCols.length) {
          const share = lostPx / pxCols.length;
          for (const c of pxCols) c.width = Math.round((c.width as number) + share);
        }
      }
      return { ...row, cols: kept };
    })
    .filter((row) => row.cols.length > 0);
  return out;
}

export function removeBlock(layout: SignatureLayout, blockId: string): SignatureLayout {
  const out = clone(layout);
  for (const row of out.rows) for (const col of row.cols) col.blocks = col.blocks.filter((b) => b.id !== blockId);
  return collapseEmpty(out);
}

/** Insère un bloc (déjà retiré de l'arbre s'il y était) à la cible. */
export function insertBlock(layout: SignatureLayout, block: LayoutBlock, target: DropTarget): SignatureLayout {
  const out = clone(layout);
  if (target.kind === 'row') {
    const at = Math.max(0, Math.min(target.index, out.rows.length));
    out.rows.splice(at, 0, { id: newId(out, 'r'), cols: [{ id: newId(out, 'c'), width: 'auto', valign: 'top', blocks: [block] }] });
    return out;
  }
  const loc = findCol(out, target.colId);
  if (!loc) return out;
  if (target.kind === 'block') {
    const at = Math.max(0, Math.min(target.index, loc.col.blocks.length));
    loc.col.blocks.splice(at, 0, block);
    return out;
  }
  // scission : nouvelle colonne à côté, largeur partagée si la colonne était en px ; refusée au-delà
  // de la profondeur maximale (l'éditeur affiche SPLIT_REFUSED_NOTE, l'arbre reste tel quel)
  if (!canSplit(out, target.colId)) return out;
  const width: number | string = typeof loc.col.width === 'number' ? Math.round(loc.col.width / 2) : 'auto';
  if (typeof loc.col.width === 'number') loc.col.width = loc.col.width - (width as number);
  const fresh: LayoutCol = { id: newId(out, 'c'), width, valign: 'top', blocks: [block] };
  loc.row.cols.splice(target.side === 'left' ? loc.colIndex : loc.colIndex + 1, 0, fresh);
  return out;
}

/** Déplace un bloc existant vers la cible ; l'index cible est celui vu AVANT le retrait du bloc. */
export function moveBlockTo(layout: SignatureLayout, blockId: string, target: DropTarget): SignatureLayout {
  const loc = findBlock(layout, blockId);
  if (!loc) return clone(layout);
  const block = clone(loc.block);
  let out = clone(layout);
  // retrait sans effondrement : la cible peut désigner la colonne d'origine
  for (const row of out.rows) for (const col of row.cols) col.blocks = col.blocks.filter((b) => b.id !== blockId);
  let t = target;
  if (t.kind === 'block' && t.colId === loc.col.id && t.index > loc.blockIndex) t = { ...t, index: t.index - 1 };
  out = insertBlock(out, block, t);
  return collapseEmpty(out);
}

export function updateBlock(layout: SignatureLayout, blockId: string, patch: Partial<LayoutBlock>): SignatureLayout {
  const out = clone(layout);
  const loc = findBlock(out, blockId);
  if (!loc) return out;
  const next = { ...loc.block, ...patch } as Record<string, unknown>;
  for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === null || next[k] === '') delete next[k];
  loc.col.blocks[loc.blockIndex] = next as unknown as LayoutBlock;
  return out;
}

export function updateCol(layout: SignatureLayout, colId: string, patch: Partial<LayoutCol>): SignatureLayout {
  const out = clone(layout);
  const loc = findCol(out, colId);
  if (!loc) return out;
  const next = { ...loc.col, ...patch } as Record<string, unknown>;
  for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === null) delete next[k];
  loc.row.cols[loc.colIndex] = next as unknown as LayoutCol;
  return out;
}

const borderKey = (side: BorderSide) => `border${side[0].toUpperCase()}${side.slice(1)}` as 'borderLeft' | 'borderRight' | 'borderTop' | 'borderBottom';
const PAD_INDEX: Record<BorderSide, number> = { top: 0, right: 1, bottom: 2, left: 3 };
const opposite: Record<BorderSide, BorderSide> = { left: 'right', right: 'left', top: 'bottom', bottom: 'top' };

/** Pose ou règle un trait : épaisseur seulement, la couleur est celle du rôle `separator` de la charte. */
export function setColBorder(layout: SignatureLayout, colId: string, side: BorderSide, width: number): SignatureLayout {
  return updateCol(layout, colId, { [borderKey(side)]: { width: Math.max(1, Math.round(width)) } });
}

/**
 * Retire une bordure de colonne SANS résidu : la bordure, la marge de ce côté de la colonne, et la
 * marge en vis-à-vis de la colonne voisine (même ligne pour gauche/droite, ligne voisine pour
 * haut/bas). Le filet entre logo et texte disparaît en un clic, sans laisser d'espacement.
 */
export function removeColBorder(layout: SignatureLayout, colId: string, side: BorderSide): SignatureLayout {
  const out = clone(layout);
  const loc = findCol(out, colId);
  if (!loc) return out;
  const zeroPad = (col: LayoutCol, s: BorderSide) => {
    if (!col.padding) return;
    const p = [...col.padding] as Padding;
    p[PAD_INDEX[s]] = 0;
    col.padding = p.every((n) => n === 0) ? undefined : p;
  };
  delete loc.col[borderKey(side)];
  zeroPad(loc.col, side);
  if (side === 'left' || side === 'right') {
    const neighbour = loc.row.cols[side === 'left' ? loc.colIndex - 1 : loc.colIndex + 1];
    if (neighbour) zeroPad(neighbour, opposite[side]);
  } else {
    const neighbourRow = out.rows[side === 'top' ? loc.rowIndex - 1 : loc.rowIndex + 1];
    if (neighbourRow) for (const c of neighbourRow.cols) zeroPad(c, opposite[side]);
  }
  return out;
}

/** Nouvelles largeurs après un glissement de la frontière entre deux colonnes voisines. */
export function resizeColumns(layout: SignatureLayout, leftId: string, rightId: string, leftPx: number, rightPx: number | 'auto'): SignatureLayout {
  const minW = 24;
  let out = updateCol(layout, leftId, { width: Math.max(minW, Math.round(leftPx)) });
  out = updateCol(out, rightId, { width: rightPx === 'auto' ? 'auto' : Math.max(minW, Math.round(rightPx)) });
  return out;
}

export function removeCol(layout: SignatureLayout, colId: string): SignatureLayout {
  const out = clone(layout);
  // On vide la colonne puis collapseEmpty l'effondre : sa largeur en px est redistribuée aux voisines.
  for (const row of out.rows) {
    for (const col of row.cols) {
      if (col.id === colId) {
        col.blocks = [];
        delete col.rows;
      }
    }
  }
  return collapseEmpty(out);
}

export function removeRow(layout: SignatureLayout, rowId: string): SignatureLayout {
  const out = clone(layout);
  out.rows = out.rows.filter((r) => r.id !== rowId);
  return out;
}

/** Monte (-1) ou descend (+1) un bloc dans sa colonne (panneau « Emplacement », deux flèches). */
export function moveBlockBy(layout: SignatureLayout, blockId: string, delta: -1 | 1): SignatureLayout {
  const loc = findBlock(layout, blockId);
  if (!loc) return clone(layout);
  const to = loc.blockIndex + delta;
  if (to < 0 || to >= loc.col.blocks.length) return clone(layout);
  const out = clone(layout);
  const col = findBlock(out, blockId)!.col;
  const [b] = col.blocks.splice(loc.blockIndex, 1);
  col.blocks.splice(to, 0, b);
  return out;
}

/** Envoie un bloc à la fin d'une autre colonne (panneau « Emplacement », menu des colonnes). */
export function moveBlockToCol(layout: SignatureLayout, blockId: string, colId: string): SignatureLayout {
  const loc = findBlock(layout, blockId);
  const dest = findCol(layout, colId);
  if (!loc || !dest || loc.col.id === colId) return clone(layout);
  return moveBlockTo(layout, blockId, { kind: 'block', colId, index: dest.col.blocks.length });
}

/** Colonnes de l'arbre avec un libellé humain : « Ligne 1, colonne 2 ». */
export function listCols(layout: SignatureLayout): { id: string; label: string }[] {
  return layout.rows.flatMap((r, i) => r.cols.map((c, j) => ({ id: c.id, label: `Ligne ${i + 1}, colonne ${j + 1}` })));
}

// ---------------------------------------------------------------- palette (phase 2 : blocs existants)

export type PaletteKey = 'logo' | 'social' | 'banner' | 'text' | `field:${FieldId}`;

export const PALETTE: { key: PaletteKey; label: string }[] = [
  { key: 'logo', label: 'Logo' },
  { key: 'field:name', label: BLOCK_LABELS.name },
  { key: 'field:title', label: BLOCK_LABELS.title },
  { key: 'field:phone_mobile', label: BLOCK_LABELS.phone_mobile },
  { key: 'field:phone_office', label: BLOCK_LABELS.phone_office },
  { key: 'field:email', label: BLOCK_LABELS.email },
  { key: 'field:address', label: BLOCK_LABELS.address },
  { key: 'field:website', label: BLOCK_LABELS.website },
  { key: 'field:legal', label: BLOCK_LABELS.legal },
  { key: 'social', label: BLOCK_LABELS.social },
  { key: 'banner', label: BLOCK_LABELS.banner },
  { key: 'text', label: 'Texte libre' },
];

export function paletteKey(block: LayoutBlock): PaletteKey | null {
  if (block.type === 'field') return `field:${block.field}`;
  if (block.type === 'logo' || block.type === 'social' || block.type === 'banner' || block.type === 'text') return block.type;
  return null;
}

/** Clés déjà présentes dans l'arbre : grisées dans la palette (on ne met pas deux fois le nom). */
export function presentKeys(layout: SignatureLayout): Set<PaletteKey> {
  const set = new Set<PaletteKey>();
  for (const r of layout.rows) for (const c of r.cols) for (const b of c.blocks) {
    const k = paletteKey(b);
    if (k) set.add(k);
  }
  return set;
}

/** Clés qu'on ne peut plus ajouter : celles déjà présentes (un bloc ne se pose qu'une fois) — sauf le texte libre, posable autant de fois qu'on veut. */
export function unavailableKeys(layout: SignatureLayout): Set<PaletteKey> {
  const set = presentKeys(layout);
  set.delete('text');
  return set;
}

/**
 * Colonne « principale » : celle qui porte le plus de blocs (la colonne de texte d'une charte
 * classique). null si l'arbre n'a aucune colonne.
 */
export function mainColId(layout: SignatureLayout): string | null {
  let best: { id: string; n: number } | null = null;
  for (const r of layout.rows) for (const c of r.cols) if (!best || c.blocks.length > best.n) best = { id: c.id, n: c.blocks.length };
  return best?.id ?? null;
}

/** Ajoute un bloc en fin de colonne (clic dans la palette) ; sans colonne, dans une nouvelle ligne en bas. */
export function appendBlock(layout: SignatureLayout, block: LayoutBlock, colId: string | null): SignatureLayout {
  const target = colId ?? mainColId(layout);
  const loc = target ? findCol(layout, target) : null;
  if (!loc) return insertBlock(layout, block, { kind: 'row', index: layout.rows.length });
  return insertBlock(layout, block, { kind: 'block', colId: loc.col.id, index: loc.col.blocks.length });
}

export const canAddKey = (layout: SignatureLayout, key: PaletteKey): boolean => !unavailableKeys(layout).has(key);

/** Profondeur de colonnes d'une colonne : 1 dans une ligne de l'arbre, 2 dans une sous-ligne. */
export function columnDepth(layout: SignatureLayout, colId: string): number {
  for (const row of layout.rows) {
    for (const col of row.cols) {
      if (col.id === colId) return 1;
      for (const sub of col.rows ?? []) if (sub.cols.some((c) => c.id === colId)) return 2;
    }
  }
  return 0;
}

export const MAX_SPLIT_DEPTH = 2;
export const SPLIT_REFUSED_NOTE = 'Scission refusée : deux niveaux de colonnes maximum, au-delà Outlook devient imprévisible.';

/**
 * Une scission crée une colonne sœur (même ligne) : elle n'augmente jamais la profondeur. Elle est
 * refusée seulement pour une colonne qui vit déjà au niveau maximal d'imbrication (sous-ligne), ou
 * qui n'existe pas.
 */
export function canSplit(layout: SignatureLayout, colId: string): boolean {
  const depth = columnDepth(layout, colId);
  return depth >= 1 && depth < MAX_SPLIT_DEPTH;
}

export function makeBlock(layout: SignatureLayout, key: PaletteKey): LayoutBlock {
  const id = newId(layout, 'b');
  if (key.startsWith('field:')) return { id, type: 'field', field: key.slice(6) as FieldId };
  if (key === 'text') return { id, type: 'text', text: 'Votre texte', role: 'body' };
  return { id, type: key as 'logo' | 'social' | 'banner' };
}

/** Variables insérables dans un texte libre, avec leur libellé pour le bouton d'insertion. */
export const TEXT_VARIABLES: { token: string; label: string }[] = [
  { token: '{{prenom}}', label: 'Prénom' },
  { token: '{{nom}}', label: 'Nom' },
  { token: '{{fonction}}', label: 'Fonction' },
  { token: '{{lieu}}', label: 'Nom du lieu' },
  { token: '{{email}}', label: 'E-mail' },
  { token: '{{tel_mobile}}', label: 'Mobile' },
  { token: '{{tel_fixe}}', label: 'Fixe' },
  { token: '{{adresse}}', label: 'Adresse' },
  { token: '{{site}}', label: 'Site' },
];

/** Libellé humain d'un bloc, pour le panneau et les infobulles. */
export function blockLabel(block: LayoutBlock): string {
  switch (block.type) {
    case 'field':
      return BLOCK_LABELS[block.field];
    case 'logo':
      return 'Logo';
    case 'social':
      return BLOCK_LABELS.social;
    case 'banner':
      return BLOCK_LABELS.banner;
    case 'text':
      return 'Texte libre';
    case 'image':
      return 'Image';
    case 'spacer':
      return 'Espace';
    case 'divider':
      return 'Filet';
  }
}
